import { Button, CloseButton, Dialog, Field, Fieldset, Input, Portal, Text } from '@chakra-ui/react';
import ApplicationErrorView from '@packages/charka-common-ui/graphql/error/application-error-view';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useTranslate } from '@tolgee/react';
import { toaster } from 'provider/src/module/chakra-ui/components/ui/toaster.tsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCreatePrintRule } from '../logic/use-create-print-rule';

interface FormValues {
  name: string;
}

export default function CreatePrintRuleDialog() {
  const { t } = useTranslate();
  const menuId = useParams().menuId ?? '';
  const branch = useFocusedBranchContext();

  const [create, { error, loading }] = useCreatePrintRule();

  const [open, setOpen] = useState(false);
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = () => {
    if (!branch.branch) {
      return;
    }

    create({
      variables: {
        input: {
          menuId,
          name: watch('name'),
          branchId: branch.branch.id,
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('print-rule.create.alert', 'Create print rule successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('print-rule.create.alert.failed', 'Create print rule failed'),
          type: 'error',
        });
      });
  };

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('print-rule.create.name', 'Name')}</Field.Label>
          <Input
            {...register('name', { required: t('print-rule.create.name-required', 'Name is required') })}
            name="name"
          />

          {errors.name && (
            <Text color="red.500" fontSize="sm">
              {errors.name.message}
            </Text>
          )}
        </Field.Root>
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
      }}
      placement={'center'}
    >
      <Dialog.Trigger asChild>
        <Button bgColor="gray.100" p={2} variant="surface">
          <Text fontWeight={600}> {t('common.button.create', 'Create')}</Text>
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title fontSize={'xl'}>{t('print-rule.dialog.create.title', 'Create Print Rule')}</Dialog.Title>
            </Dialog.Header>

            <ApplicationErrorView error={error} />

            <Dialog.Body>{FieldSet}</Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="surface" p={2}>
                  {t('common.button.cancel', 'Cancel')}
                </Button>
              </Dialog.ActionTrigger>
              <Button
                loading={loading}
                onClick={() => {
                  void handleSubmit(onSubmit)();
                }}
                p={2}
                bg={'gray.700'}
                variant={'surface'}
                color={'white'}
                type="submit"
              >
                {t('common.button.create')}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
